/* eslint-disable no-constant-binary-expression */
/* eslint-disable no-useless-catch */
/* eslint-disable @typescript-eslint/no-explicit-any */
// ** Compatible API Service for all Axios versions
// ** services/apiService.ts

import config from "@src/@core/auth/jwt/jwtDefaultConfig";
import axios from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { environmentConfig } from "@configs/environmentConfig";
import { store } from '@src/redux/store';
import { handleLogout } from '@src/redux/authentication';
import {
  isPermissionForbiddenMessage,
  MY_PERMISSIONS_PATH,
  requestPermissionRefresh,
} from '@src/utility/permissions/permissionRefresh';

// ** Types
interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
}

// ** Simple development detection
const isDevelopment =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

// ** Error Logger Service
class ErrorLogger {
  static log(error: ApiError, context?: string) {
    const timestamp = new Date().toISOString();
    const logData = {
      timestamp,
      context,
      error: {
        message: error.message,
        status: error.status,
        code: error.code,
        details: error.details,
      },
    };

    // Console logging for development
    if (isDevelopment) {
      console.group(`🚨 API Error - ${context || "Unknown"}`);
      console.error("Error Details:", logData);
      console.groupEnd();
    }

    // Here you can add external logging services
    // Example: Send to logging service like LogRocket, Sentry, etc.
    // this.sendToExternalLogger(logData)
  }

  // static async sendToExternalLogger(logData: any) {
  //   // Implementation for external logging service
  //   // Example: Sentry, LogRocket, DataDog, etc.
  // }
}

// ** API Service Class
class ApiService {
  public api: AxiosInstance;

  constructor() {
    console.log("env veriable---", environmentConfig.BACKEND_URL);
    this.api = axios.create({
      baseURL: `${environmentConfig.BACKEND_URL}/api/v1/admin` || "/api",
      timeout: 30000,
      // ✅ Removed default Content-Type header to allow FormData to work properly
      // headers: {
      //   "Content-Type": "application/json",
      // },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // ** Request Interceptor - Compatible with all axios versions
    this.api.interceptors.request.use(
      (config: any) => {
        // Add auth token if available
        const token = this.getAuthToken();
        if (token) {
          // Ensure headers object exists
          if (!config.headers) {
            config.headers = {};
          }
          config.headers["Authorization"] = `Bearer ${token}`;
        }

        // ✅ Only set JSON content-type if no content-type is set and data is not FormData
        if (
          !config.headers["Content-Type"] &&
          config.data &&
          !(config.data instanceof FormData)
        ) {
          config.headers["Content-Type"] = "application/json";
        }

        // Log request in development
        if (isDevelopment) {
          console.log(
            `📤 API Request: ${config.method?.toUpperCase()} ${config.url}`
          );
        }

        return config;
      },
      (error: AxiosError) => {
        ErrorLogger.log(
          {
            message: "Request setup failed",
            details: error.message,
          },
          "Request Interceptor"
        );
        return Promise.reject(error);
      }
    );

    // ** Response Interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log successful response in development
        if (isDevelopment) {
          console.log(
            `📥 API Response: ${response.status} ${response.config.url}`
          );
        }

        return response;
      },
      (error: AxiosError) => {
        this.handleApiError(error);
        return Promise.reject(error);
      }
    );
  }

  // private handleApiError(error: AxiosError) {
  //   const apiError: ApiError = {
  //     message: "An error occurred",
  //     status: error.response?.status,
  //     code: error.code,
  //   };

  //   if (error.response) {
  //     // Server responded with error status
  //     const { status, data } = error.response
  //     apiError.message = (data as any)?.message || `Server Error: ${status}`
  //     apiError.details = data
  //     // Handle  specific status codes
  //     switch (status) {
  //       case 401:
  //         apiError.message = 'Unauthorized access. Please login again.'
  //         // this.handleUnauthorized()
  //         break
  //       case 403:
  //         apiError.message = "Access forbidden. You do not have permission.";
  //         break;
  //       case 404:
  //         apiError.message = "Resource not found.";
  //         break;
  //       case 422:
  //         apiError.message = "Validation error. Please check your input.";
  //         break;
  //       case 500:
  //         apiError.message = "Internal server error. Please try again later.";
  //         break;
  //       default:
  //         apiError.message = `Request failed with status ${status}`;
  //     }
  //   } else if (error.request) {
  //     // Network error
  //     apiError.message = "Network error. Please check your connection.";
  //     apiError.code = "NETWORK_ERROR";
  //   } else {
  //     // Request setup error
  //     apiError.message = error.message || "Request setup failed";
  //   }

  //   // Log the error
  //   ErrorLogger.log(apiError, error.config?.url);
  // }

private handleApiError(error: AxiosError) {
  const apiError: ApiError = {
    message: "An error occurred",
    status: error.response?.status,
    code: error.code,
  };

  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    apiError.message = (data as any)?.message || `Server Error: ${status}`;
    apiError.details = data;
    
    // Handle specific status codes
    switch (status) {
      case 401: {
        apiError.message = "Unauthorized access. Please login again.";
        const requestUrl = String(error.config?.url ?? "");
        const isAuthFlowRequest =
          requestUrl.includes("/auth/verify-2fa") ||
          requestUrl.includes("/auth/resend-otp") ||
          requestUrl.includes("/auth/login");
        if (!isAuthFlowRequest) {
          this.handleUnauthorized();
        }
        break;
      }
      case 403:
        apiError.message = "Access forbidden. You do not have permission.";
        {
          const requestUrl = String(error.config?.url ?? "");
          const responseMessage = String((data as { message?: string })?.message ?? "");
          if (
            !requestUrl.includes(MY_PERMISSIONS_PATH) &&
            isPermissionForbiddenMessage(responseMessage)
          ) {
            requestPermissionRefresh();
          }
        }
        break;
      case 404:
        apiError.message = "Resource not found.";
        break;
      case 422:
        apiError.message = "Validation error. Please check your input.";
        break;
      case 500:
        apiError.message = "Internal server error. Please try again later.";
        break;
      default:
        apiError.message = `Request failed with status ${status}`;
    }
  } else if (error?.request) {
    // Network error (e.g., connection refused, server not running)
    apiError.message = "Network error. Please check your connection or try again later.";
    apiError.code = "NETWORK_ERROR";
    
    // ❌ REMOVED: Don't call handleUnauthorized on network errors
    // this.handleUnauthorized(); // This was causing logout on network issues
    
    console.warn("🔶 Network error detected - keeping user session intact");
  } else {
    // Request setup error
    apiError.message = error.message || "Request setup failed";
  }

  ErrorLogger.log(apiError, error.config?.url);
}

  // ✅ Safe token parsing with fallback
private getAuthToken(): string | null {
  const getToken = localStorage.getItem(config.storageTokenKeyName);
  if (getToken) {
    try {
      return JSON.parse(getToken); // Try JSON first
    } catch (parseError) {
      return getToken; // Fallback to plain string
    }
  }
  return null;
}

  private handleUnauthorized() {
    // Handle unauthorized access (logout, redirect, etc.)
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (path === "/login" || path === "/2fa") {
        return;
      }
      localStorage.removeItem("userData");
      localStorage.removeItem(config.storageTokenKeyName);
      localStorage.removeItem(config.storageRefreshTokenKeyName);
      window.location.href = "/login";
    }
  }

  // ** Generic API Methods with proper error handling
  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.get<T>(url, config);
      return {
        data: response.data,
        status: response.status,
        message: "Success",
      };
    } catch (error) {
      throw error;
    }
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.post<T>(url, data, config);
      return {
        data: response.data,
        status: response.status,
        message: "Success",
      };
    } catch (error) {
      throw error;
    }
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.put<T>(url, data, config);
      return {
        data: response.data,
        status: response.status,
        message: "Success",
      };
    } catch (error) {
      throw error;
    }
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.patch<T>(url, data, config);
      return {
        data: response.data,
        status: response.status,
        message: "Success",
      };
    } catch (error) {
      throw error;
    }
  }

  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.delete<T>(url, config);
      return {
        data: response.data,
        status: response.status,
        message: "Success",
      };
    } catch (error) {
      throw error;
    }
  }

  // ** Upload file method
  async uploadFile<T = any>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await this.api.post<T>(url, formData, {
        headers: {
          // ✅ Removed explicit Content-Type setting - let axios handle it automatically
          // "Content-Type": "multipart/form-data",
        },
        onUploadProgress,
      });

      return {
        data: response.data,
        status: response.status,
        message: "File uploaded successfully",
      };
    } catch (error) {
      throw error;
    }
  }

  // ** Download file method
  async downloadFile(url: string, filename?: string): Promise<void> {
    try {
      const response = await this.api.get(url, {
        responseType: "blob",
      });

      // Create blob link to download
      const blob = new Blob([response.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename || "download";
      link.click();

      // Clean up
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      throw error;
    }
  }

  // ** Set auth token method
  setAuthToken(token: string) {
    if (typeof window !== "undefined") {
      localStorage.setItem("accessToken", token);
    }
  }

  // ** Clear auth token method
  clearAuthToken() {
    if (typeof window !== "undefined") {
      localStorage.removeItem("accessToken");
    }
  }
}

// ** Create and export singleton instance
const apiService = new ApiService();
export default apiService;

// ** Export for direct axios usage if needed
export { apiService, ErrorLogger };

// ** Export types
export type { ApiResponse, ApiError };
